import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Waveform } from '@/components/ui/waveform';

const base64ToBlobChatbot = (base64: string, contentType = 'audio/mpeg'): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

function formatTimeChatbot(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
}

interface AudioPlayerProps {
  audioData: string;
  from: string;
}

export function AudioPlayer({ audioData, from }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);

  const isFromBot = from !== 'client';

  useEffect(() => {
    if (audioData) {
      const blob = base64ToBlobChatbot(audioData, 'audio/mpeg');
      const url = URL.createObjectURL(blob);
      setAudioURL(url);

      const audio = new Audio(url);
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
      });
      audioRef.current = audio;

      return () => {
        audio.pause();
        audio.remove();
        URL.revokeObjectURL(url);
      };
    }
  }, [audioData]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  // Generate simple waveform data (can be enhanced later with actual audio analysis)
  const waveformData = Array.from({ length: 40 }, () => 0.2 + Math.random() * 0.6);

  return (
    <div className="flex items-center gap-2 w-full min-w-[200px] max-w-[280px]">
      {/* Play/Pause button */}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={handlePlayPause}
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full transition-colors",
          isFromBot 
            ? "bg-gray-100 hover:bg-gray-200" 
            : "bg-primary/10 hover:bg-primary/20"
        )}
        aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
      >
        {isPlaying ? (
          <Pause className={cn("h-4 w-4", isFromBot ? "text-gray-700" : "text-white")} />
        ) : (
          <Play className={cn("h-4 w-4", isFromBot ? "text-gray-700" : "text-white")} />
        )}
      </Button>
      
      {/* Waveform */}
      <div className="flex-1 min-w-[120px]">
        <Waveform
          data={waveformData}
          height={48}
          barWidth={2}
          barGap={1}
          barRadius={1}
          fadeEdges={true}
          barColor={isFromBot ? "#9CA3AF" : "#E5E7EB"}
        />
      </div>
      
      {/* Duration display */}
      {duration > 0 && (
        <span className="flex-shrink-0 text-xs text-muted-foreground">
          {formatTimeChatbot(duration)}
        </span>
      )}
    </div>
  );
}

